import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PurchaseOrder } from '../types';
import { purchaseOrderAPI } from '../services/api';
import DocumentList from './DocumentList';

const PurchaseOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [po, setPo] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchPurchaseOrder(parseInt(id));
    }
  }, [id]);

  const fetchPurchaseOrder = async (poId: number) => {
    try {
      setLoading(true);
      const response = await purchaseOrderAPI.getById(poId);
      setPo(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch purchase order');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (value: string | undefined) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(value));
  };

  const handleDocumentsUpdate = () => {
    if (id) {
      fetchPurchaseOrder(parseInt(id));
    }
  };

  if (loading) return <div className="loading">Loading purchase order...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!po) return <div className="error">Purchase order not found</div>;

  const fundsUtilization = parseFloat(po.funds_utilization_percentage || '0');

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>Purchase Order: {po.po_number}</h2>
            <p style={{ color: '#7f8c8d', margin: '0.5rem 0 0 0' }}>
              Customer: {po.customer_details?.name || `Customer #${po.customer}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span className={`status-badge status-${po.status}`}>
              {po.status}
            </span>
            <Link to={`/purchase-orders/${po.id}/edit`} className="btn btn-primary">
              Edit PO
            </Link>
            <button onClick={() => navigate('/purchase-orders')} className="btn btn-secondary">
              Back to List
            </button>
          </div>
        </div>

        {/* Alerts Section */}
        {(po.is_funds_low || po.is_pop_expiring_soon || po.is_pop_expired) && (
          <div style={{ marginBottom: '2rem' }}>
            {po.is_funds_low && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fee',
                color: '#c0392b',
                borderRadius: '8px',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>💰</span>
                <strong>Low Funds Warning:</strong> This PO has low remaining funds
              </div>
            )}
            {po.is_pop_expired ? (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fee',
                color: '#c0392b',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>🚫</span>
                <strong>Period of Performance Expired:</strong> This PO's period of performance has ended
              </div>
            ) : po.is_pop_expiring_soon && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#fef5e7',
                color: '#d68910',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                <strong>Period of Performance Expiring:</strong> {po.days_until_pop_expires} days remaining
              </div>
            )}
          </div>
        )}

        {/* Financial Summary */}
        {po.total_value && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Financial Summary</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{
                padding: '1.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px solid #e9ecef'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                  Total Value
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#2c3e50' }}>
                  {formatCurrency(po.total_value)}
                </div>
              </div>

              <div style={{
                padding: '1.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: `2px solid ${fundsUtilization >= 90 ? '#e74c3c' : fundsUtilization >= 75 ? '#f39c12' : '#27ae60'}`
              }}>
                <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                  Remaining Funds
                </div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: fundsUtilization >= 90 ? '#e74c3c' : fundsUtilization >= 75 ? '#f39c12' : '#27ae60'
                }}>
                  {formatCurrency(po.remaining_funds)}
                </div>
              </div>

              <div style={{
                padding: '1.5rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px solid #e9ecef'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                  Utilized
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#2c3e50' }}>
                  {fundsUtilization.toFixed(1)}%
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#ecf0f1',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  marginTop: '0.5rem'
                }}>
                  <div style={{
                    width: `${Math.min(fundsUtilization, 100)}%`,
                    height: '100%',
                    backgroundColor: fundsUtilization >= 90 ? '#e74c3c' : fundsUtilization >= 75 ? '#f39c12' : '#27ae60',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              {po.average_monthly_billing && parseFloat(po.average_monthly_billing) > 0 && (
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '2px solid #e9ecef'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                    Avg Monthly Billing
                  </div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#2c3e50' }}>
                    {formatCurrency(po.average_monthly_billing)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Period of Performance */}
        {(po.start_date || po.end_date) && (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Period of Performance</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem'
            }}>
              <div style={{
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                  Start Date
                </div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {formatDate(po.start_date)}
                </div>
              </div>

              <div style={{
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                  End Date
                </div>
                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                  {formatDate(po.end_date)}
                </div>
              </div>

              {po.start_date && po.end_date && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                    Duration
                  </div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                    {Math.ceil((new Date(po.end_date).getTime() - new Date(po.start_date).getTime()) / (1000 * 60 * 60 * 24))} days
                  </div>
                </div>
              )}

              {po.days_until_pop_expires !== null && po.days_until_pop_expires !== undefined && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: po.is_pop_expired ? '#fee' : po.is_pop_expiring_soon ? '#fef5e7' : '#f8f9fa',
                  borderRadius: '8px',
                  border: po.is_pop_expired ? '2px solid #e74c3c' : po.is_pop_expiring_soon ? '2px solid #f39c12' : 'none'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                    Days Remaining
                  </div>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: po.is_pop_expired ? '#c0392b' : po.is_pop_expiring_soon ? '#d68910' : '#2c3e50'
                  }}>
                    {po.days_until_pop_expires < 0 ? `Expired ${Math.abs(po.days_until_pop_expires)} days ago` : po.days_until_pop_expires}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ marginBottom: '1rem' }}>Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <strong>Issue Date:</strong> {formatDate(po.issue_date)}
            </div>
            <div>
              <strong>Payment Terms:</strong> {po.payment_terms_display}
            </div>
            {po.projects_details && po.projects_details.length > 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Projects:</strong>{' '}
                {po.projects_details.map(p => `${p.code} - ${p.name}`).join(', ')}
              </div>
            )}
            {po.description && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Description:</strong> {po.description}
              </div>
            )}
            {po.notes && (
              <div style={{ gridColumn: '1 / -1' }}>
                <strong>Notes:</strong> {po.notes}
              </div>
            )}
          </div>
        </div>

        {/* Documents Section */}
        <div>
          <h3 style={{ marginBottom: '1rem' }}>Documents</h3>
          <DocumentList
            documents={po.documents || []}
            onDelete={handleDocumentsUpdate}
          />
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'center',
            color: '#7f8c8d',
            fontSize: '0.9rem'
          }}>
            To upload or manage documents, use the <Link to={`/purchase-orders/${po.id}/edit`} style={{ color: '#3498db', textDecoration: 'none', fontWeight: 'bold' }}>Edit</Link> page
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrderDetail;

